import React from "react";
import { useContext, useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";

//MUI
import Box from "@mui/material/Box";
import List from "@mui/material/List";
import { useTheme, useMediaQuery, Typography, Collapse } from "@mui/material";
import Divider from "@mui/material/Divider";

//Open Layers
import OlFormatWKT from "ol/format/WKT";
import OlPoint from "ol/geom/Point";

//Custom Components
import MapContext from "@/components/Map/MapContext";
import LDTStageItems from "./LDTStageItems";


//Types
import { DCRecord } from "@/@types/lib/dataController";
import { ILDTPane } from "@/@types/views/GIS/map";
import { InfoPaneActionButtons, InfoPaneShareActionButton, InfoPaneUrlActionButton, InfoPaneCommentActionButton, InfoPaneActionButton } from "../PublicActionButtons";
import { Feature } from "ol";
import { Geometry } from "ol/geom";

type LDTPaneType =
  {
    LDTPane: ILDTPane;
    mobile?: boolean;
    onExplore?: (feat: any) => void;
    drawerStage: number;
    pane: number | undefined;
    open: boolean;
  }

function LDTPane(props: LDTPaneType) {
  const { t } = useTranslation();

  const { mobile, onExplore, drawerStage, pane, open, LDTPane } = props;

  const [oldDrawerStage, setOldDrawerStage] = useState(1);
  const [selectedId, setSelectedId] = useState<number | undefined>(undefined);
  const [selectedFeature, setSelectedFeature] = useState<Feature<Geometry> | undefined>(undefined);

  const mapContext = useContext(MapContext);

  const wktFormatter = new OlFormatWKT();

  const textBox = useRef<HTMLInputElement>(null);

  const theme = useTheme();
  const isMobile: boolean = useMediaQuery(theme.breakpoints.down("md"));

  const handleClick = (evt: React.MouseEvent<HTMLDivElement> | null, item: DCRecord) => {
    zoomTo(item);
    const id = item["stage_id"] as number;
    setSelectedId(id);
    const feature = wktFormatter.readFeature(item["wkt"], {
              dataProjection: "EPSG:3765",
              featureProjection: "EPSG:3857"
            })
    feature.setProperties({'color': LDTPane.data.record.highlight_color});
    setSelectedFeature(feature);
  };

  const zoomTo = (item: DCRecord) => {
    const wkt = item["wkt"];
    if (wkt) {
      const wktFormatter = new OlFormatWKT();
      const feat = wktFormatter.readFeature(wkt, {
        dataProjection: "EPSG:3765",
        featureProjection: "EPSG:3857"
      });
      feat.setProperties({'color': LDTPane.data.record.highlight_color});
      feat.setProperties({'ldt_id' : item["id"]});

      if (onExplore && feat) {
        onExplore(feat);
      }

      if (mapContext && mapContext.map) {
        const view = mapContext.map.getView();
        const extent = feat.getGeometry()?.getExtent();

        const padding = isMobile ? [20, 20, 300, 20] : [20, 20, 20, 20]
        if (extent)
          view.fit(extent, { padding: padding, duration: 500, maxZoom: 15 });
      }
    }
  };

  useEffect(() => {
    if(LDTPane !== undefined && LDTPane.selectedLDTStageId !== 0) {
      const initialRecord = (LDTPane?.data.record?.stages as DCRecord[]).find((x: DCRecord) => {
        x.id = LDTPane.selectedLDTStageId as number;
      });
      if(initialRecord !== undefined) {
        handleClick(null, initialRecord)
      }
    }
  }, [LDTPane]);

  useEffect(() => {
    if (pane === 0 && open && drawerStage === oldDrawerStage) {
      if (textBox.current) {
        setTimeout(() => {
          textBox.current?.focus();
        }, 100)
      }
    }
    else if (isMobile && pane === 0 && oldDrawerStage === 0 && drawerStage !== 0) {
      setTimeout(() => {
        textBox.current?.focus();
      }, 100)
    }

    setOldDrawerStage(drawerStage);
  }, [open, pane, drawerStage]);

  useEffect(() => {
    const stages = LDTPane?.data.record?.stages as any[];
    if (stages) {
      stages.forEach(stage => {
        if (LDTPane.selectedLDTStageId == stage.stage_id) {
          handleClick(null, stage)
        }
      })
    }
  }, [LDTPane]);


  const textFnNaziv = (item: DCRecord) => {
    const naziv = item["naziv"] as string;
    const naziv_ttoken = item["naziv_ttoken"]
    return naziv_ttoken ? t(naziv_ttoken as string) as string
      : naziv ? naziv
        : null;
  }

  const publicSearcPaneItemsCommonProps = {
    onClick: handleClick,
    onAddToRoute: () => {},
    selectedId: selectedId
  }

  // @ts-ignore
  const selectedStage = LDTPane?.data.record?.stages.find(x => x.stage_id === selectedId);
  const secondaryId = selectedStage?.stage_id;

  return (
    <>
      <Box width={"100%"} p={2}>
        <Box>
          <Typography variant="h6" component="h2">
            {t(LDTPane?.data.record?.naziv as string)}
          </Typography>
        </Box>
        <InfoPaneActionButtons>
          {/* <InfoPaneActionButton
            title_ttoken={"buttons.share"}
            icon={<Share sx={{ transform: "scale(0.8)" }} color="primary" fontSize="large"></Share>}
            onClick={(evt) => { }}
          /> */}
          <InfoPaneShareActionButton
            object_type="ldt"
            // @ts-ignore
            infoPane={{data: {...LDTPane?.data, feature: selectedFeature}, secondaryId: secondaryId }}
          />
          {/*}
          <InfoPaneActionButton
            title_ttoken={"buttons.comment"}
            icon={<ChatBubbleOutlineOutlinedIcon sx={{ transform: "scale(0.8)" }} color="primary" fontSize="large" />}
            onClick={(evt) => { }}
          /> */}
          {/* <InfoPaneShareActionButton
          infoPane={infoPane}
          object_type="poi"
        />
        <InfoPaneCommentActionButton
          objekt_type="poi"
          handleGoToComments={handleShowCommentPane}
          // @ts-ignore
          data={infoPane?.data}
        /> */}
          {LDTPane?.data.record.info_url !== null ?
            <InfoPaneUrlActionButton
              url={LDTPane?.data.record.info_url as string}
            />
            :
            null
          }
        </InfoPaneActionButtons>
      </Box>
      <Divider variant="middle" sx={{ my: 2 }} />
      <List sx={{
        width: "100%",
        bgcolor: "background.paper",
        position: "relative",
        overflow: "auto",
        height: { xs: drawerStage < 2 ? "30vh" : "calc( 100vh - 230px )", md: "calc( 100vh -  255px )" },
        "& ul": { padding: 0 },
        transition: "all 0.3s"
      }}>
        <LDTStageItems
          {...publicSearcPaneItemsCommonProps}
          items={LDTPane?.data.record?.stages as DCRecord[]}
          primaryTextFn={textFnNaziv}
          selectedId={selectedId}
          onClick={handleClick}
        />
      </List>
    </>
  );
}

export default LDTPane;